import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { isBinderType, EXTENSION_STORE_URLS } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import { useFamilyCategories } from '../hooks/useFamilyCategories';
import { useNavCollapsed } from '../hooks/useNavCollapsed';
import { categoryIconName } from '../lib/categoryIcons';
import { HeartLogo } from './hbs/HeartLogo';
import { Icon, type IconName } from './hbs/Icon';

interface NavEntry {
  label: string;
  icon: IconName;
  to?: string;
  /** External URL — opens in a new tab. Mutually exclusive with `to`.
   *  Used for the App Downloads group's store links. */
  externalUrl?: string;
  count?: number;
  inert?: boolean;
  indicator?: boolean;
  /** Render a louder red dot (vs the subtle teal `indicator` dot). Reserved
   *  for things the user needs to *act on*. Today: pending family invites
   *  or admin finalisations of email-invite signups. */
  attention?: boolean;
}

interface NavGroup {
  label: string;
  icon: IconName;
  children: NavEntry[];
}

const STORAGE_KEY = (label: string): string => `hbs:nav:expanded:${label}`;

function loadExpanded(label: string, defaultValue: boolean): boolean {
  if (typeof localStorage === 'undefined') return defaultValue;
  const stored = localStorage.getItem(STORAGE_KEY(label));
  if (stored === null) return defaultValue;
  return stored === 'true';
}

function saveExpanded(label: string, value: boolean): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY(label), String(value));
}

function rowClass(active: boolean, inert?: boolean): string {
  const base =
    'flex items-center gap-3 px-3 py-[9px] rounded-hbs-md text-[14px] justify-center lg2:justify-start';
  if (inert) {
    return `${base} font-medium text-hbs-text-mute opacity-60 cursor-not-allowed`;
  }
  if (active) {
    return `${base} font-semibold bg-hbs-surface-alt text-hbs-text`;
  }
  return `${base} font-medium text-hbs-text-mute hover:bg-hbs-surface-alt`;
}

function Row({ entry }: { entry: NavEntry }): JSX.Element {
  const showDot = entry.attention || entry.indicator;
  // Icon-only rows carry no visible text, so the hover tooltip IS the label.
  // Only while collapsed — see useNavCollapsed.
  const tip = useNavCollapsed() ? entry.label : undefined;
  const content = (active: boolean): JSX.Element => (
    <>
      <Icon
        name={entry.icon}
        size={18}
        strokeWidth={active ? 2 : 1.75}
        className={active ? 'text-hbs-primary' : 'text-hbs-text-mute'}
      />
      <span className="hidden lg2:inline flex-1 truncate">{entry.label}</span>
      {showDot && (
        <span
          aria-label={entry.attention
            ? 'This section needs your attention'
            : 'You have pending invitations'}
          className={`inline-block rounded-full ${
            entry.attention ? 'w-2 h-2 bg-hbs-danger' : 'w-1.5 h-1.5 bg-hbs-primary'
          }`}
        />
      )}
      {typeof entry.count === 'number' && (
        <span
          aria-hidden="true"
          className="hidden lg2:inline text-[12px] font-mono text-hbs-text-mute"
        >
          {entry.count}
        </span>
      )}
    </>
  );

  if (entry.externalUrl) {
    return (
      <a
        href={entry.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={entry.label}
        title={tip}
        className={rowClass(false, false)}
      >
        {content(false)}
      </a>
    );
  }

  if (entry.inert || !entry.to) {
    return (
      <button
        id={`sidebar-entry-${entry.label.toLowerCase().replace(/\s+/g, '-')}`}
        type="button"
        disabled
        aria-label={entry.label}
        title={tip}
        className={rowClass(false, true)}
      >
        {content(false)}
      </button>
    );
  }

  return (
    <NavLink
      to={entry.to}
      end
      aria-label={entry.label}
      title={tip}
      className={({ isActive }) => rowClass(isActive, false)}
    >
      {({ isActive }) => content(isActive)}
    </NavLink>
  );
}

function Group({ group }: { group: NavGroup }): JSX.Element {
  const tip = useNavCollapsed() ? group.label : undefined;
  const location = useLocation();
  const matchesChild = group.children.some(
    (child) => child.to !== undefined && location.pathname.startsWith(child.to),
  );
  const [expanded, setExpanded] = useState<boolean>(
    () => loadExpanded(group.label, matchesChild),
  );

  useEffect(() => {
    if (matchesChild && !expanded) setExpanded(true);
  }, [matchesChild]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(): void {
    const next = !expanded;
    setExpanded(next);
    saveExpanded(group.label, next);
  }

  return (
    <div>
      <button
        id={`sidebar-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-label={group.label}
        title={tip}
        className={rowClass(false, false)}
      >
        <Icon
          name={group.icon}
          size={18}
          strokeWidth={1.75}
          className="text-hbs-text-mute"
        />
        <span className="hidden lg2:inline flex-1 truncate">{group.label}</span>
        <Icon
          name={expanded ? 'chevronDown' : 'chevronRight'}
          size={14}
          className="hidden lg2:inline text-hbs-text-mute"
        />
      </button>
      {expanded && (
        <div className="flex flex-col gap-px lg2:pl-4">
          {group.children.map((child) => (
            <Row key={child.label} entry={child} />
          ))}
        </div>
      )}
    </div>
  );
}

/** A collapsible family node (pattern B): the family name toggles open to
 *  reveal its Binder categories followed by the Family contacts catch-all. The
 *  rail browses — it carries no category management (that's in Family Settings).
 *  Expand state persists per family, like the Tools group. */
function FamilyNode({
  org,
  categories,
}: {
  org: { id: string; name?: string };
  categories: { categoryId: string; name: string; icon?: string }[];
}): JSX.Element {
  const location = useLocation();
  const base = `/orgs/${org.id}/binder`;
  const matchesChild = location.pathname.startsWith(base);
  const familyName = org.name ?? 'Your family';
  const tip = useNavCollapsed() ? familyName : undefined;
  const [expanded, setExpanded] = useState<boolean>(
    () => loadExpanded(`family:${org.id}`, matchesChild),
  );

  useEffect(() => {
    if (matchesChild && !expanded) setExpanded(true);
  }, [matchesChild]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggle(): void {
    const next = !expanded;
    setExpanded(next);
    saveExpanded(`family:${org.id}`, next);
  }

  return (
    <div>
      <button
        id={`sidebar-family-${org.id}`}
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-label={familyName}
        title={tip}
        className={rowClass(false, false)}
      >
        <Icon name="people" size={18} strokeWidth={1.75} className="text-hbs-text-mute" />
        <span className="hidden lg2:inline flex-1 truncate">{familyName}</span>
        <Icon
          name={expanded ? 'chevronDown' : 'chevronRight'}
          size={14}
          className="hidden lg2:inline text-hbs-text-mute"
        />
      </button>
      {expanded && (
        <div className="flex flex-col gap-px lg2:pl-4">
          {categories.map((cat) => (
            <Row
              key={cat.categoryId}
              entry={{ label: cat.name, icon: categoryIconName(cat.icon), to: `${base}/${cat.categoryId}` }}
            />
          ))}
          <Row entry={{ label: 'Family contacts', icon: 'contact', to: `${base}/contacts` }} />
        </div>
      )}
    </div>
  );
}

function Divider(): JSX.Element {
  return <div aria-hidden="true" className="my-2 border-t border-hbs-border" />;
}

export default function Sidebar(): JSX.Element {
  const { items, orgs, pendingInvites, pendingFinalizations, lock, logout, loginEmail } = useSafe();
  // Lock / Sign out swap their text for an icon below lg2; the tooltip stands in
  // for the label exactly there, and stays off when the text is visible.
  const navCollapsed = useNavCollapsed();
  const footerTip = (label: string): string | undefined => (navCollapsed ? label : undefined);
  // Hide families that have lost active coverage (trial expired / sub lapsed).
  // Fail-open: absent coverage (pre-backfill) is treated as covered.
  const visibleOrgs = orgs.filter((org) => org.covered !== false);
  const familyAttention = pendingInvites.length > 0 || pendingFinalizations.length > 0;
  const navigate = useNavigate();

  async function handleLogout(): Promise<void> {
    await logout();
    navigate('/auth', { replace: true });
  }

  const categoriesByOrg = useFamilyCategories();

  // Contacts live on their own surface, so they don't count toward — or appear
  // in — My Safe. `type` may be absent on legacy items (they're logins), so the
  // guard treats "no type" as a safe item.
  const contactCount = items.filter((i) => i.type === 'contact' && !i.orgId).length;
  // Payment cards live on their own My Payment Cards surface (personal-only in
  // v1), so they don't count toward — or appear in — My Safe.
  const cardCount = items.filter((i) => i.type === 'card' && !i.orgId).length;
  // Binder types (contacts, documents, schedules) and payment cards live on
  // their own surfaces, not the Safe — exclude them from the My Safe count so
  // it matches the list.
  const safeCount = items.filter((i) => !isBinderType(i.type) && i.type !== 'card').length;

  // Pattern B (nav-decisions §2): personal Safe + Contacts + Payment Cards
  // pinned at the top, never inside a family scope.
  const pinnedTop: NavEntry[] = [
    { label: 'My Safe', icon: 'safe', to: '/safe', count: safeCount },
    { label: 'My Contacts', icon: 'contact', to: '/contacts', count: contactCount },
    { label: 'My Payment Cards', icon: 'card', to: '/cards', count: cardCount },
  ];

  // One link below all families → the existing Families wrapper (create-family /
  // add-seats + per-family management, which now also owns category management).
  const familySettings: NavEntry = {
    label: 'Family settings', icon: 'people', to: '/orgs', count: visibleOrgs.length, attention: familyAttention,
  };

  // Third section: Security Checkup (moved here from the pinned-top section),
  // Password Generator, Data, and Settings. The former "Tools" expander is gone
  // — its Password Generator becomes a top-level row and its Import now lives on
  // the Data page (alongside Export + Restore, which moved off Settings).
  // Security Checkup carries no count by design — a calm pull surface, never a
  // badge (no-nag, checkup spec §8).
  const checkupRow: NavEntry = { label: 'Security Checkup', icon: 'checklist', to: '/checkup' };
  const generatorRow: NavEntry = { label: 'Password Generator', icon: 'sparkle', to: '/tools' };
  const dataRow: NavEntry = { label: 'Data', icon: 'folder', to: '/data' };
  const settingsRow: NavEntry = { label: 'Settings', icon: 'settings', to: '/settings' };

  // Fourth section: Help (the marketing support section) + app-download links.
  const helpRow: NavEntry = {
    label: 'Help',
    icon: 'launch',
    externalUrl: 'https://heybesafe.com/support',
  };

  const appDownloadsGroup: NavGroup = {
    label: 'App Downloads',
    icon: 'download',
    children: [
      // URLs come from the shared map so this list cannot drift from the one
      // the onboarding checklist uses. Edge has its own listing — the Chrome
      // Web Store build does install in Edge, but it is the wrong store to send
      // an Edge user to and it installs under a different extension id, which
      // defeats the install probe. See shared/src/extensionLinks.ts.
      {
        label: 'Chrome',
        icon: 'launch',
        externalUrl: EXTENSION_STORE_URLS.chrome,
      },
      {
        label: 'Edge',
        icon: 'launch',
        externalUrl: EXTENSION_STORE_URLS.edge,
      },
      {
        label: 'Firefox',
        icon: 'launch',
        externalUrl: EXTENSION_STORE_URLS.firefox,
      },
      {
        label: 'Android',
        icon: 'launch',
        externalUrl: 'https://play.google.com/store/apps/details?id=com.milcreto.heybesafe',
      },
      {
        label: 'iOS',
        icon: 'launch',
        externalUrl: 'https://apps.apple.com/us/app/hey-be-safe/id6780194779',
      },
    ],
  };

  return (
    <aside
      data-testid="sidebar"
      className="w-[60px] lg2:w-[248px] flex-shrink-0 flex flex-col h-full bg-hbs-surface border-r border-hbs-border"
    >
      {/* Brand row */}
      <div
        aria-label="Hey Be Safe"
        className="flex items-center gap-2 px-4 pt-4 pb-2 justify-center lg2:justify-start"
      >
        <HeartLogo size={26} />
        <span
          aria-hidden="true"
          className="hidden lg2:inline font-display font-semibold text-[17px] tracking-hbs-tight text-hbs-text"
        >
          Hey Be Safe
        </span>
      </div>

      {/* Nav — pattern B: pinned personal, collapsible families, then settings */}
      <nav className="flex-1 overflow-auto px-3 pb-4 flex flex-col gap-px">
        {pinnedTop.map((entry) => (
          <Row key={entry.label} entry={entry} />
        ))}
        <Divider />
        {visibleOrgs.map((org) => (
          <FamilyNode key={org.id} org={org} categories={categoriesByOrg.get(org.id) ?? []} />
        ))}
        <Row entry={familySettings} />
        <Divider />
        <Row entry={checkupRow} />
        <Row entry={generatorRow} />
        <Row entry={dataRow} />
        <Row entry={settingsRow} />
        <Divider />
        <Row entry={helpRow} />
        <Group group={appDownloadsGroup} />
      </nav>

      {/* Footer */}
      <div className="mt-auto border-t border-hbs-border p-3 flex flex-col items-center gap-2 lg2:flex-row lg2:items-center">
        {loginEmail && (
          <>
            <div
              className="w-8 h-8 rounded-full bg-hbs-primary flex items-center justify-center text-white text-[13px] font-bold flex-shrink-0"
              title={`Signed in as ${loginEmail}`}
              aria-label={`Signed in as ${loginEmail}`}
            >
              {loginEmail.charAt(0).toUpperCase()}
            </div>
            {/* Empty spacer keeps Lock/Sign-out buttons pinned to the right of
                the expanded sidebar (the email-text div used to do this). */}
            <div className="hidden lg2:block flex-1" />
          </>
        )}
        <button
          type="button"
          id="sidebar-lock-button" onClick={lock}
          aria-label="Lock"
          title={footerTip('Lock')}
          className="w-8 h-8 inline-flex items-center justify-center rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt lg2:w-auto lg2:h-auto lg2:px-2 lg2:py-1"
        >
          <Icon name="lock" size={16} className="lg2:hidden" />
          <span className="hidden lg2:inline text-[11px] font-semibold">Lock</span>
        </button>
        <button
          type="button"
          id="sidebar-sign-out-button" onClick={handleLogout}
          aria-label="Sign out"
          title={footerTip('Sign out')}
          className="w-8 h-8 inline-flex items-center justify-center rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt lg2:w-auto lg2:h-auto lg2:px-2 lg2:py-1"
        >
          <Icon name="signOut" size={16} className="lg2:hidden" />
          <span className="hidden lg2:inline text-[11px] font-semibold">Sign out</span>
        </button>
      </div>
    </aside>
  );
}
